import { useCallback, useEffect, useId, useMemo, useRef, useState, type KeyboardEvent as ReactKeyboardEvent, type ReactElement, type ReactNode } from 'react'
import { cn } from '../../lib/cn'
import { CONTROL_H, controlBase } from './Field'
import { Popover } from './Popover'
import { edgeIndex, initialIndex, moveIndex, typeaheadIndex, typeaheadQuery, type NavOption } from './listboxNav'

export interface SelectOption<T extends string = string> {
  value: T
  label: ReactNode
  /** Second line under the label, shown in the popover only. */
  hint?: ReactNode
  disabled?: boolean
  /** Draw a divider above this option (light grouping). */
  divider?: boolean
}

const SIZES = { md: `${CONTROL_H.md} text-[13.5px] max-sm:text-base`, sm: `${CONTROL_H.sm} text-[12.5px] rounded-[7px] px-2.5 max-sm:text-[13px]` } as const
const TYPEAHEAD_RESET_MS = 500

/** Plain text an option is matched against by typeahead. */
function optionText<T extends string>(o: SelectOption<T>): string {
  return typeof o.label === 'string' ? o.label : o.value
}

/**
 * Popover select: a `role=combobox` trigger over a `role=listbox` panel. DOM focus stays on the
 * trigger and the active option is tracked with `aria-activedescendant`, so the control also works
 * inside `Modal`'s focus trap (the panel is portalled out of the modal subtree).
 *
 * Width comes from a wrapper (`<div className="w-44"><Select … /></div>`), never from `className`:
 * the trigger sets `w-full` and Tailwind emits `.w-full` after `.w-auto`.
 */
export function Select<T extends string = string>({ value, onChange, options, size = 'md', variant = 'default', placeholder, label, id, disabled, invalid, className, 'aria-describedby': describedBy }: {
  value: T
  onChange(value: T): void
  options: SelectOption<T>[]
  size?: 'sm' | 'md'
  variant?: 'default' | 'inline'
  placeholder?: ReactNode
  label?: string
  id?: string
  disabled?: boolean
  invalid?: boolean
  className?: string
  'aria-describedby'?: string
}): ReactElement {
  const auto = useId()
  const triggerId = id ?? auto
  const listId = `${triggerId}-list`
  const optionId = (i: number) => `${listId}-opt-${i}`
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(0)
  const ref = useRef<HTMLButtonElement>(null)
  const list = useRef<HTMLDivElement>(null)
  const buffer = useRef({ text: '', at: 0 })

  const nav = useMemo<NavOption[]>(() => options.map(o => ({ value: o.value, text: optionText(o), disabled: o.disabled })), [options])
  const selected = options.find(o => o.value === value)
  const close = useCallback(() => setOpen(false), [])
  const openList = useCallback(() => {
    setActive(initialIndex(nav, value))
    setOpen(true)
  }, [nav, value])

  // Keep the active option visible while the arrow keys move it.
  useEffect(() => {
    if (!open) return
    list.current?.querySelectorAll<HTMLElement>('[role=option]')[active]?.scrollIntoView({ block: 'nearest' })
  }, [open, active])

  const pick = (i: number) => {
    const o = options[i]
    if (!o || o.disabled) return
    onChange(o.value)
    close()
    ref.current?.focus()
  }

  const onKeyDown = (e: ReactKeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault()
      if (!open) openList()
      else setActive(a => moveIndex(nav, a, e.key === 'ArrowDown' ? 1 : -1))
      return
    }
    if (e.key === 'Home' || e.key === 'End') {
      if (!open) return
      e.preventDefault()
      setActive(edgeIndex(nav, e.key === 'Home' ? 'first' : 'last'))
      return
    }
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      if (!open) openList()
      else pick(active)
      return
    }
    if (e.key === 'Tab') {
      if (open) close()
      return
    }
    // Escape is left to Popover, which closes the panel and returns focus to the trigger.
    if (e.key.length !== 1 || e.ctrlKey || e.metaKey || e.altKey) return
    e.preventDefault()
    const now = Date.now()
    const expired = now - buffer.current.at > TYPEAHEAD_RESET_MS
    const text = expired ? e.key : buffer.current.text + e.key
    buffer.current = { text, at: now }
    const from = open ? (expired ? active + 1 : active) : initialIndex(nav, value)
    const next = typeaheadIndex(nav, typeaheadQuery(text), from)
    if (next < 0) return
    if (!open) setOpen(true)
    setActive(next)
  }

  return (
    <>
      <button
        ref={ref} id={triggerId} type="button" role="combobox" aria-haspopup="listbox"
        aria-expanded={open} aria-controls={listId} aria-activedescendant={open ? optionId(active) : undefined}
        aria-label={label} aria-describedby={describedBy} aria-invalid={invalid || undefined}
        disabled={disabled} onClick={() => (open ? close() : openList())} onKeyDown={onKeyDown}
        className={cn(
          'flex items-center justify-between gap-2 text-left',
          variant === 'default' && controlBase,
          variant === 'default' && SIZES[size],
          variant === 'inline' && 'rounded-lg px-2.5 py-1.5 text-xs font-medium text-ink-300 hover:bg-cream-200 hover:text-ink-100 transition-colors disabled:bg-transparent disabled:opacity-50 w-auto min-w-0',
          className
        )}
      >
        <span className="truncate">{selected ? selected.label : placeholder ?? '—'}</span>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="shrink-0 text-ink-400" aria-hidden="true"><path d="M6 9l6 6 6-6" /></svg>
      </button>
      <Popover anchor={ref} open={open} onClose={close} width={200} matchWidth label={label}>
        <div ref={list} role="listbox" id={listId} aria-label={label} className="flex flex-col gap-0.5">
          {options.map((o, i) => (
            <div key={o.value}>
              {o.divider && <div className="my-1 h-px bg-line" />}
              <button
                type="button" role="option" id={optionId(i)}
                aria-selected={o.value === value} aria-disabled={o.disabled || undefined} disabled={o.disabled}
                onPointerEnter={() => setActive(i)} onClick={() => pick(i)}
                className={cn(
                  'flex min-h-[34px] w-full items-center justify-between gap-3 rounded-[7px] px-2.5 py-1 text-left text-[13px] font-medium max-sm:min-h-11 max-sm:text-sm',
                  o.value === value ? 'bg-ginger-100 text-ink-100'
                    : i === active ? 'bg-cream-200 text-ink-100'
                    : 'text-ink-100 hover:bg-cream-200',
                  o.disabled && 'opacity-50'
                )}
              >
                <span className="min-w-0">
                  <span className="block truncate">{o.label}</span>
                  {o.hint && <span className="block text-xs font-normal text-ink-500">{o.hint}</span>}
                </span>
                {o.value === value && <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="shrink-0 text-ginger-500" aria-hidden="true"><path d="M5 12l5 5 9-10" /></svg>}
              </button>
            </div>
          ))}
        </div>
      </Popover>
    </>
  )
}
